/* eslint-disable @typescript-eslint/no-var-requires */
import {getFirebaseEnvironment} from "@buildwithflux/constants";
import {
    createImmerPatchAdapter,
    createPartLibrary,
    createSnapshotLibrary,
    DocumentService,
    PcbConnectivityGraph,
    PcbPrimitiveStore,
    PcbTracingGraph,
} from "@buildwithflux/core";
import {ClientFunctionsAdapter} from "@buildwithflux/firebase-functions-adapter";
import {
    ClientFirestoreAdapter,
    createClientConverters,
    FirestoreCache,
    getFirebaseConfigForSpecificEnvironment,
} from "@buildwithflux/firestore-compatibility-layer";
import {getFirebaseCompatApp} from "@buildwithflux/firestore-compatibility-layer/src/app";
import {setGlobalFallbackLogger, silentLogger} from "@buildwithflux/logger";
import {
    DefaultMetadataGenerator,
    OrganizationUid,
    StripeClientMode,
    userJobParsers,
    UserUid,
    type AliffBoard,
} from "@buildwithflux/models";
import {PlanEntitlementService} from "@buildwithflux/plans";
import {
    ClientIdProvider,
    createFirestorePartRepository,
    createFirestorePartVersionRepository,
    createFirestoreSharedDocumentRepository,
    createFirestoreStarredDocumentRepository,
    DocumentSnapshotRepository,
    FirestoreAccountFollowingRepository,
    FirestoreActionRecordRepository,
    FirestoreAutoLayoutJobMetadataRepository,
    FirestoreAutoLayoutJobRepository,
    FirestoreAutoPlacementJobRepository,
    FirestoreChatRepository,
    FirestoreCommentRepository,
    FirestoreDocumentPricingRepository,
    FirestoreDocumentRepository,
    FirestoreEducationalDomainsRepository,
    FirestoreEnterpriseRepository,
    FirestoreHandleRepository,
    FirestoreKnowledgeBaseRepository,
    FirestoreProjectDocRepository,
    FirestoreOrganizationPrivateMetadataRepository,
    FirestoreOrganizationRepository,
    FirestoreReviewRepository,
    FirestoreUserAnalyticsRepository,
    FirestoreUserJobRepository,
    FirestoreUserPrivateMetadataRepository,
    FirestoreUserRepository,
    FirestoreUserUiStateRepository,
    StaticPartVersionRepository,
} from "@buildwithflux/repositories";
import {APP_VERSION, areWeTestingWithJest, defaultCdnConfig} from "@buildwithflux/shared";
import {connectAuthEmulator, getAuth} from "firebase/auth";

import {createCopilotChatStoreHook} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";
import {FileOrganizationStorageHelper} from "modules/account";
import {createAccountInformationStore} from "modules/account/AccountInformationStore";
import {createAppStoreAndHook} from "modules/app/state/app";
import {FirebaseAuthService, FirebaseCurrentUserService, SignUpService} from "modules/auth";
import {FluxAuthClaimsService} from "modules/auth/services/FluxAuthClaimsService";
import {TrialReactivationService} from "modules/auth/services/trialReactivation";
import {createAlgoliaSearchClientStoreHook} from "modules/auth/state/algoliaClient";
import {createCurrentUserStoreHook} from "modules/auth/state/currentUser";
import {createOrganizationStoreHook} from "modules/auth/state/organization";
import {createAuthPermissionsStore} from "modules/auth/state/permissions";
import {createTypesenseSearchClientStoreHook} from "modules/auth/state/typesenseClient";
import {createUserStore} from "modules/auth/state/user";
import {
    DeepPcbAutoLayoutApiClient,
    DeepPcbAutoLayoutBoardExporter,
    DeepPcbAutoLayoutBoardFetcher,
    DeepPcbAutoLayoutEventBus,
    DeepPcbAutoLayoutIterationFactory,
    DeepPcbAutoLayoutIterationProcessor,
    DeepPcbAutoLayoutReviewService,
    DeepPcbAutoLayoutService,
    DeepPcbAutoLayoutStoreService,
    type AutoLayoutBoardExporter,
} from "modules/auto_layout";
import type {DeepPcbBoardExportMetadata} from "modules/auto_layout/services";
import {AutoPlacementService} from "modules/auto_placement";
import {HttpAutopilotService} from "modules/autopilot";
import {createChangeHistoryStoreAndHook} from "modules/changeHistory/state/changeHistory";
import {ClipboardService} from "modules/clipboard/ClipboardService";
import {createUseRemoteUserIsTypingStateHook} from "modules/comments/remoteUserTypingState";
import {createUseCommentStoreHook} from "modules/comments/store";
import {createPinoLoggerForEnvironment} from "modules/common/logger";
import {workerRunConnectivityDetection} from "modules/connectivity/pcbConnectivityGraphWorkerAdapter";
import {createCopilotExecutionPlanStore} from "modules/copilot-execution-plan/store";
import {createDialogsStore} from "modules/dialogs/state/dialogs";
import {
    createActionRecordService,
    createDocumentConfigService,
    createDocumentMetaSyncService,
    createDocumentSaveService,
    createPatchHandlerService,
} from "modules/document/effects";
import {createDocumentStoreAndHook, type UndoRedoStore} from "modules/document/state/document";
import {type DocumentServices, ActionEventBus} from "modules/document/state/serviceNotifier";
import {createDocumentMetaStore} from "modules/documentMeta/state/documentMeta";
import {createFeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";
import {LaunchDarklyService} from "modules/feature_flags/LaunchDarklyService";
import {createKeyboardShortcutStore} from "modules/keyboard_shortcut/store";
import {ActionBakeObserver} from "modules/observers/ActionBakeObserver";
import {DocumentObserver} from "modules/observers/DocumentObserver";
import {createOnboardingDialogStore} from "modules/onboarding/store";
import {createPartNotificationService} from "modules/part/effects";
import {createPartStoreAndHook} from "modules/part/state/part";
import {createPartVersionStoreAndHook} from "modules/partVersion/state/partVersion";
import {createCreditReportStoreHook} from "modules/payments/state/credit";
import {createPaymentUrlStoreHook} from "modules/payments/state/url";
import {createPcbStackupEditorDialogStore} from "modules/pcb_stackup_editor_dialog/store";
import {DocumentPreviewService} from "modules/preview/DocumentPreviewService";
import {ClientPricingService} from "modules/pricing";
import {FirestoreProfileHelper, ProjectDataEnrichmentService} from "modules/profile";
import {
    createReactivitySubscriptions,
    createUseAllPcbNodes,
    createUsePcbNode,
    createUsePcbNodeBakedRules,
} from "modules/reactivity";
import {createReviewStore} from "modules/review/store";
import {createAutoZoomToFitStore} from "modules/schematic_editor/auto_zoom_to_fit/store";
import {AnalyticsStorage} from "modules/storage_engine/AnalyticsStorage";
import {BrowserStaticPartVersionStorageAdapter} from "modules/storage_engine/cdn";
import {BrowserDocumentSnapshotStorageAdapter} from "modules/storage_engine/cdn/BrowserDocumentSnapshotStorageAdapter";
import {NoOpDocumentSnapshotStorageAdapter} from "modules/storage_engine/cdn/NoOpDocumentSnapshotStorageAdapter";
import {NoOpStaticPartVersionStorageAdapter} from "modules/storage_engine/cdn/NoOpStaticPartVersionStorageAdapter";
import {CommentStorage} from "modules/storage_engine/CommentStorage";
import AlgoliaConnector from "modules/storage_engine/connectors/AlgoliaConnector";
import {TypesenseConnector} from "modules/storage_engine/connectors/TypesenseConnector";
import {FirestoreDocumentStorage} from "modules/storage_engine/DocumentStorage";
import {S3FileStorage} from "modules/storage_engine/FileStorage";
import {AnalyticsEventHelper} from "modules/storage_engine/helpers/AnalyticsEventHelper";
import {DocumentStorageHelper} from "modules/storage_engine/helpers/DocumentStorageHelper";
import {PartStorageHelper} from "modules/storage_engine/helpers/PartStorageHelper";
import {OrganizationProfileUpdater, UserProfileUpdater} from "modules/storage_engine/helpers/ProfileUpdater";
import {UserStorageHelper} from "modules/storage_engine/helpers/UserStorageHelper";
import {LRULocalStorage} from "modules/storage_engine/LRULocalStorage";
import {PartProjectCreationService} from "modules/storage_engine/PartProjectCreationService";
import {PartStorage} from "modules/storage_engine/PartStorage";
import {ProjectInviteStorage} from "modules/storage_engine/ProjectInviteStorage";
import {UserStorage} from "modules/storage_engine/UserStorage";
import {PcbBakedGoodsManager} from "modules/stores/pcb/PcbBakedGoodsManager/PcbBakedGoodsManager";
import {createPersistedDocumentUiStore} from "modules/stores/ui/persistedStore";
import {createUseDocumentUiStoreHook} from "modules/stores/ui/store";
import {changeCaptureUnitOfWorkCreator} from "modules/unit_of_work/ChangeCaptureUnitOfWork";
import {createUserUiStateStore} from "modules/user_ui_states/store";
import {UserCodeRuntimeWrapper} from "modules/usercode_runtime/UserCodeRuntimeWrapper";

import type {ContainerBindings} from "./container";
import {connectToFirebaseFunctions} from "./firebaseFunctions";

/**
 * Now we define a recipe for how to instantiate each container service
 */

// Shared mutable services object and event bus for wiring document store to effect services.
// Services are lazily created after the store, so we use a mutable object that gets populated
// as each service binding is resolved. The store's notifyServicesOfAction() reads from this
// object by reference, so it always sees the latest registered services.
// These are reset when documentStore is resolved to ensure fresh state per container instance.
let sharedDocumentServices: DocumentServices = {};
let sharedActionEventBus = new ActionEventBus();
let sharedUndoRedoStore: UndoRedoStore;

export const defaultBindings: ContainerBindings = {
    shutdown(services) {
        return async () => {
            if ("logger" in services) {
                services.logger.debug("Beginning container shutdown");
            }

            if ("useOnboardingDialogStore" in services) {
                services.useOnboardingDialogStore.getState().shutdown?.();
            }

            // Dispose effect services before Firebase is terminated to prevent late async operations.
            // IMPORTANT: We use Object.hasOwn() to check if the service was instantiated WITHOUT
            // triggering lazy initialization. The container uses a Proxy with only a `get` trap,
            // so Object.hasOwn() checks the underlying target object directly (where instantiated
            // services are cached). This gives us a per-container "instantiated?" check that:
            // 1. Doesn't trigger lazy initialization of services that were never used
            // 2. Is accurate per-container (unlike module-level tracking which is process-global)
            // 3. Prevents "import after Jest environment torn down" errors during test teardown
            if (Object.hasOwn(services, "partNotificationService")) {
                services.partNotificationService.dispose();
            }
            if (Object.hasOwn(services, "documentConfigService")) {
                services.documentConfigService.dispose();
            }
            if (Object.hasOwn(services, "documentSaveService")) {
                services.documentSaveService.dispose();
            }
            if (Object.hasOwn(services, "actionRecordService")) {
                services.actionRecordService.dispose();
            }
            if (Object.hasOwn(services, "patchHandlerService")) {
                services.patchHandlerService.dispose();
            }
            if (Object.hasOwn(services, "documentMetaSyncService")) {
                services.documentMetaSyncService.dispose();
            }

            const toShutDown = [
                "algoliaConnector",
                "typesenseConnector",
                "signUpService",
                "currentUserService",
                "launchDarklyService",
            ] as const;

            for (const service of toShutDown) {
                if (service in services) {
                    await services[service].shutdown?.();
                }
            }

            if ("firestoreForEmulator" in services) {
                await services.firestoreForEmulator.terminate();
            }

            if ("firestoreForCurrentEnvironment" in services) {
                await services.firestoreForCurrentEnvironment.terminate();
            }

            if ("firebaseAppForEmulator" in services) {
                await services.firebaseAppForEmulator.delete(); // This only renders this instance unusable
            }

            if ("firebaseAppForCurrentEnvironment" in services) {
                await services.firebaseAppForCurrentEnvironment.delete(); // This only renders this instance unusable
            }

            const shutDown = services as Record<string, unknown>;
            delete shutDown.algoliaConnector;
            delete shutDown.useOnboardingDialogStore;
            delete shutDown.currentUserService;
            delete shutDown.firestoreForEmulator;
            delete shutDown.firestoreForCurrentEnvironment;
            delete shutDown.firebaseAppForEmulator;
            delete shutDown.firebaseAppForCurrentEnvironment;
            delete shutDown.featureFlags;
        };
    },

    // Firebase
    firebaseEnvironment() {
        return getFirebaseEnvironment();
    },
    firebaseAuth(services) {
        return services.firebaseEnvironment === "emulated"
            ? services.firebaseAuthForEmulator
            : services.firebaseAuthForCurrentEnvironment;
    },
    firebaseAuthForEmulator({firebaseAppForEmulator}) {
        const connection = getAuth(firebaseAppForEmulator);
        connectAuthEmulator(connection, "http://127.0.0.1:9099");
        return connection;
    },
    firebaseAuthForCurrentEnvironment({firebaseAppForCurrentEnvironment}) {
        return getAuth(firebaseAppForCurrentEnvironment);
    },
    firebaseAppConfig({firebaseEnvironment}) {
        return getFirebaseConfigForSpecificEnvironment(firebaseEnvironment);
    },
    firebaseApp(services) {
        return services.firebaseEnvironment === "emulated"
            ? services.firebaseAppForEmulator
            : services.firebaseAppForCurrentEnvironment;
    },
    firebaseAppForEmulator() {
        const config = getFirebaseConfigForSpecificEnvironment("emulated");
        return getFirebaseCompatApp(config, config.projectId);
    },
    firebaseAppForCurrentEnvironment({firebaseAppConfig}) {
        return getFirebaseCompatApp(firebaseAppConfig, firebaseAppConfig.projectId);
    },
    firestore(services) {
        return services.firebaseEnvironment === "emulated"
            ? services.firestoreForEmulator
            : services.firestoreForCurrentEnvironment;
    },
    firestoreForEmulator({firebaseAppForEmulator, firestoreSettings}) {
        const firestore = firebaseAppForEmulator.firestore();

        firestore.useEmulator("127.0.0.1", 8080);

        firestore.settings({
            // This is important to avoid runtime errors on whenever a property is
            // undefined. Previously, we ran all data thru a recursive function
            // removeUndefinedFields. The same setting is the common convention in
            // our cloud functions.
            ignoreUndefinedProperties: true,
            merge: true,

            ...firestoreSettings,
        });

        return firestore;
    },
    firestoreForCurrentEnvironment({firebaseAppForCurrentEnvironment, firestoreSettings}) {
        const firestore = firebaseAppForCurrentEnvironment.firestore();

        firestore.settings({
            // This is important to avoid runtime errors on whenever a property is
            // undefined. Previously, we ran all data thru a recursive function
            // removeUndefinedFields. The same setting is the common convention in
            // our cloud functions.
            ignoreUndefinedProperties: true,
            merge: true,

            ...firestoreSettings,
        });

        return firestore;
    },
    firestoreSettings() {
        return {
            experimentalAutoDetectLongPolling: true,
        }; // note: this binding is overridden in tests
    },
    firestoreConverters({metadataGenerator}) {
        return createClientConverters(metadataGenerator);
    },
    functionsCentral(services) {
        return connectToFirebaseFunctions(services, "us-central1", services.firebaseEnvironment === "emulated");
    },
    functionsWest(services) {
        return connectToFirebaseFunctions(services, "us-west2", services.firebaseEnvironment === "emulated");
    },
    functionsWest1(services) {
        return connectToFirebaseFunctions(services, "us-west1", services.firebaseEnvironment === "emulated");
    },

    // Auth stuff
    firebaseAuthService({firebaseAuth}) {
        return new FirebaseAuthService(firebaseAuth);
    },
    currentUserService({firebaseAuthService, userRepository, userPrivateMetadataRepository}) {
        return new FirebaseCurrentUserService(
            firebaseAuthService,
            userRepository,
            userPrivateMetadataRepository,
            silentLogger,
        );
    },
    algoliaConnector({functionsAdapter, currentUserService, logger}) {
        return new AlgoliaConnector(functionsAdapter, currentUserService, logger);
    },
    typesenseConnector({functionsAdapter, currentUserService, logger}) {
        return new TypesenseConnector(functionsAdapter, currentUserService, logger);
    },
    signUpService({firebaseAuthService, firestoreAdapter, logger, useFeatureFlagsStore}) {
        return new SignUpService(firebaseAuthService, firestoreAdapter, useFeatureFlagsStore, logger);
    },
    trialReactivationService({
        currentUserService,
        functionsAdapter,
        planEntitlement,
        analyticsStorage,
        logger,
        useFeatureFlagsStore,
    }) {
        return new TrialReactivationService(
            currentUserService,
            functionsAdapter,
            planEntitlement,
            analyticsStorage,
            logger,
            StripeClientMode.enum.live,
            // Fail-safe: returns undefined if flag not loaded, letting backend be authoritative
            () => useFeatureFlagsStore.getState().trialReactivationConfig,
        );
    },
    fluxAuthService({functionsAdapter, currentUserService, logger}) {
        const service = new FluxAuthClaimsService(functionsAdapter, currentUserService, logger);
        service.initialize();
        return service;
    },

    // Adapters
    firestoreAdapter({firestore, metadataGenerator, firestoreConverters, logger}) {
        return new ClientFirestoreAdapter(firestore, metadataGenerator, firestoreConverters, logger);
    },
    functionsAdapter({functionsCentral, functionsWest, functionsWest1}) {
        return new ClientFunctionsAdapter(functionsCentral, functionsWest, functionsWest1);
    },
    /**
     * Only connect to the real live CDN if we're not currently testing.
     */
    staticPartVersionStorageAdapter(services) {
        if (areWeTestingWithJest()) {
            return new NoOpStaticPartVersionStorageAdapter();
        } else {
            return new BrowserStaticPartVersionStorageAdapter(defaultCdnConfig, services.firebaseAuth);
        }
    },
    documentSnapshotStorageAdapter(services) {
        if (areWeTestingWithJest()) {
            return new NoOpDocumentSnapshotStorageAdapter();
        } else {
            return new BrowserDocumentSnapshotStorageAdapter(defaultCdnConfig, services.firebaseAuth, services.logger);
        }
    },

    // Layout
    pcbBakedGoodsManager({
        logger,
        pcbPrimitiveStore,
        documentService,
        pcbConnectivityGraph,
        pcbTracingGraph,
        useFeatureFlagsStore,
        documentStore,
    }) {
        return new PcbBakedGoodsManager(
            documentService,
            pcbPrimitiveStore,
            pcbConnectivityGraph,
            pcbTracingGraph,
            useFeatureFlagsStore,
            logger,
            documentStore,
        );
    },

    // Document
    documentService() {
        return new DocumentService(changeCaptureUnitOfWorkCreator);
    },
    documentPreviewService({documentStore, actionBakeObserver}) {
        return new DocumentPreviewService(documentStore, actionBakeObserver);
    },

    // Reactivity
    reactivitySubscriptions({documentService}) {
        return createReactivitySubscriptions(documentService);
    },

    // Repos
    autoLayoutJobMetadataRepository({firestoreAdapter, logger}) {
        return new FirestoreAutoLayoutJobMetadataRepository(firestoreAdapter, logger);
    },
    autoLayoutJobRepository({firestoreAdapter, logger}) {
        return new FirestoreAutoLayoutJobRepository(firestoreAdapter, logger);
    },
    autoPlacementJobRepository({firestoreAdapter, logger}) {
        return new FirestoreAutoPlacementJobRepository(firestoreAdapter, logger);
    },
    actionRecordRepository({firestoreAdapter, metadataGenerator, clientIdProvider}) {
        return new FirestoreActionRecordRepository(firestoreAdapter, metadataGenerator, clientIdProvider);
    },
    commentRepository({firestoreAdapter}) {
        return new FirestoreCommentRepository(firestoreAdapter);
    },
    chatRepository({firestoreAdapter, logger}) {
        return new FirestoreChatRepository(firestoreAdapter, logger);
    },
    projectDocRepository({firestoreAdapter, logger}) {
        return new FirestoreProjectDocRepository(firestoreAdapter, logger);
    },
    knowledgeBaseRepository({firestoreAdapter}) {
        return new FirestoreKnowledgeBaseRepository(firestoreAdapter);
    },
    userUiStateRepository({firestoreAdapter}) {
        return new FirestoreUserUiStateRepository(firestoreAdapter);
    },
    documentRepository({firestoreAdapter, functionsAdapter, logger}) {
        return new FirestoreDocumentRepository(firestoreAdapter, functionsAdapter, logger);
    },
    documentPricingRepository({firestoreAdapter, logger}) {
        return new FirestoreDocumentPricingRepository(firestoreAdapter, logger);
    },
    organizationRepository({firestoreAdapter, userRepository, organizationPrivateMetadataRepository, logger}) {
        return new FirestoreOrganizationRepository(
            firestoreAdapter,
            userRepository,
            organizationPrivateMetadataRepository,
            logger,
        );
    },
    organizationPrivateMetadataRepository({firestoreAdapter, logger}) {
        return new FirestoreOrganizationPrivateMetadataRepository(firestoreAdapter, logger);
    },
    partRepository({firestoreAdapter}) {
        return createFirestorePartRepository(firestoreAdapter);
    },
    reviewRepository({firestoreAdapter}) {
        return new FirestoreReviewRepository(firestoreAdapter);
    },
    partVersionRepository({firestoreAdapter}) {
        return createFirestorePartVersionRepository(firestoreAdapter);
    },
    sharedDocumentRepository({firestoreAdapter}) {
        return createFirestoreSharedDocumentRepository(firestoreAdapter);
    },
    starredDocumentRepository({firestoreAdapter, documentRepository}) {
        return createFirestoreStarredDocumentRepository(firestoreAdapter, documentRepository);
    },
    accountFollowingRepository({firestoreAdapter}) {
        return new FirestoreAccountFollowingRepository(firestoreAdapter);
    },
    staticPartVersionRepository({staticPartVersionStorageAdapter}) {
        return new StaticPartVersionRepository(staticPartVersionStorageAdapter);
    },
    documentSnapshotRepository({documentSnapshotStorageAdapter}) {
        return new DocumentSnapshotRepository(documentSnapshotStorageAdapter);
    },
    userAnalyticsRepository({firestoreAdapter}) {
        return new FirestoreUserAnalyticsRepository(firestoreAdapter);
    },
    userJobRepository({firestoreAdapter, logger}) {
        return new FirestoreUserJobRepository(firestoreAdapter, logger, userJobParsers);
    },
    educationalDomainsRepository({firestoreAdapter}) {
        return new FirestoreEducationalDomainsRepository(firestoreAdapter);
    },

    // Storage
    analyticsStorage({
        currentUserService,
        userAnalyticsRepository,
        useOrganizationStore,
        documentService,
        usePersistedDocumentUiStore,
        useFeatureFlagsStore,
        useAuthPermissionsStore,
        documentStore,
        logger,
    }) {
        return new AnalyticsStorage(
            currentUserService,
            userAnalyticsRepository,
            useOrganizationStore,
            documentService,
            usePersistedDocumentUiStore,
            useFeatureFlagsStore,
            useAuthPermissionsStore,
            documentStore,
            logger,
        );
    },
    commentStorage({firestore, firestoreAdapter, logger, clientIdProvider}) {
        return new CommentStorage(firestore, firestoreAdapter, logger, clientIdProvider);
    },
    documentStorage({
        documentRepository,
        logger,
        functionsAdapter,
        analyticsStorage,
        firestore,
        firestoreAdapter,
        clientIdProvider,
        snapshotLibrary,
        partStorage,
    }) {
        return new FirestoreDocumentStorage(
            documentRepository,
            logger,
            functionsAdapter,
            analyticsStorage,
            firestore,
            firestoreAdapter,
            clientIdProvider,
            snapshotLibrary,
            partStorage,
        );
    },
    enterpriseRepository({firestoreAdapter, logger}) {
        return new FirestoreEnterpriseRepository(firestoreAdapter, logger);
    },
    fileStorage({analyticsStorage, functionsAdapter}) {
        return new S3FileStorage({}, analyticsStorage, functionsAdapter);
    },
    metadataGenerator({clientIdProvider}) {
        return new DefaultMetadataGenerator(APP_VERSION, clientIdProvider.clientId, "client");
    },
    partLibrary({
        partRepository,
        partVersionRepository,
        staticPartVersionRepository,
        documentRepository,
        functionsAdapter,
    }) {
        return createPartLibrary(
            partRepository,
            partVersionRepository,
            staticPartVersionRepository,
            documentRepository,
            undefined,
            async (data) => {
                await functionsAdapter.saveDocumentSnapshot(data);
            },
        );
    },
    snapshotLibrary({documentSnapshotRepository, logger}) {
        return createSnapshotLibrary(documentSnapshotRepository, logger);
    },
    partStorage({partRepository, documentRepository, partVersionRepository, analyticsStorage, logger, partLibrary}) {
        return new PartStorage(
            partLibrary,
            documentRepository,
            partRepository,
            partVersionRepository,
            analyticsStorage,
            logger,
        );
    },
    partProjectCreationService({documentStorage, partStorage, documentRepository}) {
        return new PartProjectCreationService(documentStorage, partStorage, documentRepository);
    },
    projectInviteStorage({firestore, firestoreAdapter, logger, clientIdProvider}) {
        return new ProjectInviteStorage(firestore, firestoreAdapter, logger, clientIdProvider);
    },
    userStorage({firestore, firestoreAdapter, logger, analyticsStorage, clientIdProvider}) {
        return new UserStorage(firestore, firestoreAdapter, logger, analyticsStorage, clientIdProvider);
    },
    userRepository({firestoreAdapter, logger}) {
        return new FirestoreUserRepository(firestoreAdapter, logger);
    },
    handleRepository({firestoreAdapter}) {
        return new FirestoreHandleRepository(firestoreAdapter);
    },
    userPrivateMetadataRepository({firestoreAdapter}) {
        return new FirestoreUserPrivateMetadataRepository(firestoreAdapter);
    },
    lruLocalStorage() {
        return new LRULocalStorage();
    },

    // Plans
    planEntitlement({logger}) {
        return new PlanEntitlementService(logger);
    },

    // Autopilot
    autopilotService({
        fluxAuthService,
        currentUserService,
        useCopilotChatStore,
        chatRepository,
        logger,
        useAuthPermissionsStore,
    }) {
        return new HttpAutopilotService(
            currentUserService,
            useCopilotChatStore,
            chatRepository,
            fluxAuthService,
            logger,
            useAuthPermissionsStore,
        );
    },

    // Auto Layout
    autoLayoutEventBus({logger}) {
        return new DeepPcbAutoLayoutEventBus(logger);
    },
    autoLayoutApiClient({autoLayoutEventBus, functionsAdapter, fluxAuthService, useFeatureFlagsStore, logger}) {
        return new DeepPcbAutoLayoutApiClient(
            autoLayoutEventBus,
            logger,
            functionsAdapter,
            fluxAuthService,
            useFeatureFlagsStore,
        );
    },
    autoLayoutBoardFetcher({autoLayoutEventBus, logger}) {
        return new DeepPcbAutoLayoutBoardFetcher(autoLayoutEventBus, logger);
    },
    autoLayoutBoardExporter({
        autoLayoutEventBus,
        autoLayoutStoreService,
        pcbPrimitiveStore,
        documentService,
        currentUserService,
        documentStore,
        pcbTracingGraph,
        logger,
    }) {
        return new DeepPcbAutoLayoutBoardExporter(
            autoLayoutEventBus,
            logger,
            autoLayoutStoreService,
            pcbPrimitiveStore,
            documentStore,
            documentService,
            currentUserService,
            pcbTracingGraph,
        );
    },
    autoLayoutIterationFactory({documentService, logger, documentStore}) {
        return new DeepPcbAutoLayoutIterationFactory(documentService, logger, documentStore);
    },
    autoLayoutIterationProcessor({
        autoLayoutEventBus,
        autoLayoutStoreService,
        autoLayoutBoardFetcher,
        autoLayoutIterationFactory,
        documentService,
        documentPreviewService,
        logger,
    }) {
        return new DeepPcbAutoLayoutIterationProcessor(
            autoLayoutEventBus,
            logger,
            autoLayoutStoreService,
            autoLayoutBoardFetcher,
            autoLayoutIterationFactory,
            documentService,
            documentPreviewService,
        );
    },
    autoLayoutReviewService({
        autoLayoutEventBus,
        autoLayoutStoreService,
        documentService,
        reviewRepository,
        analyticsStorage,
        logger,
    }) {
        return new DeepPcbAutoLayoutReviewService(
            autoLayoutEventBus,
            logger,
            autoLayoutStoreService,
            documentService,
            reviewRepository,
            analyticsStorage,
        );
    },
    autoLayoutStoreService() {
        return new DeepPcbAutoLayoutStoreService();
    },
    autoLayoutService({
        autoLayoutEventBus,
        autoLayoutStoreService,
        autoLayoutApiClient,
        autoLayoutBoardExporter,
        autoLayoutIterationProcessor,
        autoLayoutReviewService,
        autoLayoutJobRepository,
        autoLayoutJobMetadataRepository,
        actionBakeObserver,
        documentPreviewService,
        currentUserService,
        useFeatureFlagsStore,
        analyticsStorage,
        logger,
        useDocumentMetaStore,
        documentStore,
    }) {
        return new DeepPcbAutoLayoutService(
            autoLayoutEventBus,
            logger,
            autoLayoutStoreService,
            autoLayoutApiClient,
            autoLayoutBoardExporter as AutoLayoutBoardExporter<AliffBoard, DeepPcbBoardExportMetadata>,
            autoLayoutIterationProcessor,
            autoLayoutReviewService,
            autoLayoutJobRepository,
            autoLayoutJobMetadataRepository,
            actionBakeObserver,
            documentPreviewService,
            currentUserService,
            useFeatureFlagsStore,
            analyticsStorage,
            useDocumentMetaStore,
            documentStore,
        );
    },

    autoPlacementService({
        autoPlacementJobRepository,
        currentUserService,
        fluxAuthService,
        documentPreviewService,
        appStore,
        documentStore,
    }) {
        return new AutoPlacementService(
            autoPlacementJobRepository,
            currentUserService,
            fluxAuthService,
            documentPreviewService,
            appStore,
            documentStore,
        );
    },

    // Part effects
    partNotificationService({appStore, partStore, documentStore, documentSaveService}) {
        return createPartNotificationService({
            appStore,
            partStore,
            documentStore,
            documentSaveService,
        });
    },

    // Document effects
    documentConfigService({documentStorage, documentStore}) {
        const service = createDocumentConfigService({documentStorage, documentStore});
        sharedDocumentServices.documentConfigService = service;
        return service;
    },
    documentSaveService({documentStorage, appStore, documentStore}) {
        const service = createDocumentSaveService({documentStorage, appStore, documentStore});
        sharedDocumentServices.documentSaveService = service;
        return service;
    },
    actionRecordService(services) {
        const {appStore, currentUserService, useAuthPermissionsStore, documentStore} = services;
        const service = createActionRecordService({
            appStore,
            currentUserService,
            useAuthPermissionsStore,
            services,
            documentStore,
        });
        sharedDocumentServices.actionRecordService = service;
        return service;
    },
    patchHandlerService(services) {
        const {appStore, documentStore} = services;
        const service = createPatchHandlerService({appStore, services, documentStore});
        sharedDocumentServices.patchHandlerService = service;
        return service;
    },
    documentMetaSyncService({documentStore, useDocumentMetaStore}) {
        void documentStore;
        const service = createDocumentMetaSyncService({useDocumentMetaStore});
        sharedDocumentServices.documentMetaSyncService = service;
        return service;
    },

    // Helpers
    pcbPrimitiveStore() {
        return new PcbPrimitiveStore();
    },
    pcbConnectivityGraph({pcbPrimitiveStore}) {
        return new PcbConnectivityGraph(pcbPrimitiveStore, workerRunConnectivityDetection);
    },
    pcbTracingGraph() {
        return new PcbTracingGraph();
    },
    immerPatchAdapter() {
        return createImmerPatchAdapter();
    },
    analyticsEventHelper({analyticsStorage, logger}) {
        return new AnalyticsEventHelper(analyticsStorage, logger.child({name: "AnalyticsEventHelper"}));
    },
    partStorageHelper({fileStorage}) {
        return new PartStorageHelper(fileStorage);
    },
    userCodeRuntimeWrapper() {
        return new UserCodeRuntimeWrapper();
    },
    userStorageHelper({fileStorage}) {
        return new UserStorageHelper(fileStorage);
    },
    documentStorageHelper({fileStorage}) {
        return new DocumentStorageHelper(fileStorage);
    },
    organizationStorageHelper({fileStorage}) {
        return new FileOrganizationStorageHelper(fileStorage);
    },
    profileHelper({handleRepository, userRepository, organizationRepository, functionsAdapter, logger}) {
        return new FirestoreProfileHelper(
            handleRepository,
            userRepository,
            organizationRepository,
            functionsAdapter,
            logger,
        );
    },
    projectDataEnrichmentService({firestoreAdapter, chatRepository, documentPricingRepository, logger}) {
        return new ProjectDataEnrichmentService(firestoreAdapter, chatRepository, documentPricingRepository, logger);
    },
    userProfileUpdater({userStorage, currentUserService, logger}) {
        return (userUid: UserUid) => new UserProfileUpdater(userUid, userStorage, currentUserService, logger);
    },
    organizationProfileUpdater({organizationRepository, currentUserService, logger}) {
        return (organizationUid: OrganizationUid) =>
            new OrganizationProfileUpdater(organizationUid, organizationRepository, currentUserService, logger);
    },
    documentObserver({documentService, immerPatchAdapter, useFeatureFlagsStore, documentStore}) {
        return new DocumentObserver(documentService, useFeatureFlagsStore, immerPatchAdapter, documentStore);
    },
    actionBakeObserver() {
        return new ActionBakeObserver();
    },
    // Store hook factories
    useAccountInformationStore({userRepository, organizationRepository, enterpriseRepository}) {
        return createAccountInformationStore(userRepository, organizationRepository, enterpriseRepository);
    },
    useUserStore({currentUserService, userPrivateMetadataRepository, analyticsStorage, userStorage}) {
        return createUserStore(currentUserService, userPrivateMetadataRepository, analyticsStorage, userStorage);
    },
    useCurrentUserStore({currentUserService, firebaseAuthService}) {
        return createCurrentUserStoreHook(currentUserService, firebaseAuthService, silentLogger);
    },
    useAlgoliaSearchClientStore({algoliaConnector}) {
        return createAlgoliaSearchClientStoreHook(algoliaConnector);
    },
    useTypesenseSearchClientStore({typesenseConnector}) {
        return createTypesenseSearchClientStoreHook(typesenseConnector);
    },
    useCommentStore({currentUserService, commentRepository, logger}) {
        return createUseCommentStoreHook(currentUserService, commentRepository, logger);
    },
    useUserUiStateStore({currentUserService, userUiStateRepository, useFeatureFlagsStore, logger}) {
        return createUserUiStateStore(currentUserService, userUiStateRepository, useFeatureFlagsStore, logger);
    },
    useRemoteUserIsTypingState(_services) {
        return createUseRemoteUserIsTypingStateHook();
    },
    useDocumentUiStore(_services) {
        return createUseDocumentUiStoreHook();
    },
    usePersistedDocumentUiStore(_services) {
        return createPersistedDocumentUiStore();
    },
    useOnboardingDialogStore({
        currentUserService,
        userRepository,
        userPrivateMetadataRepository,
        organizationRepository,
        analyticsStorage,
        functionsAdapter,
        useFeatureFlagsStore,
    }) {
        return createOnboardingDialogStore({
            currentUserService,
            userRepository,
            userPrivateMetadataRepository,
            organizationRepository,
            analyticsStorage,
            functionsAdapter,
            featureFlagsStore: useFeatureFlagsStore,
            logger: silentLogger,
        });
    },
    useOrganizationStore({currentUserService, organizationRepository, enterpriseRepository, functionsAdapter}) {
        return createOrganizationStoreHook(
            currentUserService,
            organizationRepository,
            enterpriseRepository,
            functionsAdapter,
            silentLogger,
        );
    },
    useCopilotChatStore({
        currentUserService,
        chatRepository,
        documentStore,
        documentRepository,
        knowledgeBaseRepository,
        documentService,
        logger,
        partLibrary,
        functionsAdapter,
        useFeatureFlagsStore,
    }) {
        return createCopilotChatStoreHook({
            currentUserService,
            documentStore,
            documentService,
            documentRepository,
            chatRepository,
            knowledgeBaseRepository,
            logger,
            partLibrary,
            functionsAdapter,
            useFeatureFlagsStore,
        });
    },
    usePaymentUrlStore({functionsAdapter, logger}) {
        return createPaymentUrlStoreHook(functionsAdapter, logger);
    },
    usePcbNode({reactivitySubscriptions}) {
        return createUsePcbNode(reactivitySubscriptions);
    },
    usePcbNodeBakedRules({reactivitySubscriptions}) {
        return createUsePcbNodeBakedRules(reactivitySubscriptions);
    },
    useAllPcbNodes({reactivitySubscriptions}) {
        return createUseAllPcbNodes(reactivitySubscriptions);
    },
    useCreditReportStore({functionsAdapter, currentUserService, analyticsStorage, logger}) {
        return createCreditReportStoreHook(
            functionsAdapter,
            currentUserService,
            analyticsStorage,
            logger,
            silentLogger,
        );
    },
    useKeyboardShortcutStore(_services) {
        return createKeyboardShortcutStore();
    },
    useReviewStore() {
        return createReviewStore();
    },
    usePcbStackupEditorDialogStore() {
        return createPcbStackupEditorDialogStore();
    },
    useFeatureFlagsStore({launchDarklyService}) {
        return createFeatureFlagsStore(launchDarklyService);
    },
    useFluxClaimsStore({fluxAuthService}) {
        return fluxAuthService.createFluxClaimsHook();
    },
    useCopilotExecutionPlanStore(_services) {
        return createCopilotExecutionPlanStore();
    },
    useAutoZoomToFitStore(_services) {
        return createAutoZoomToFitStore();
    },
    useAuthPermissionsStore(_services) {
        return createAuthPermissionsStore();
    },
    useDialogsStore(_services) {
        return createDialogsStore();
    },
    usePartVersionStore({partVersionStore}) {
        // Use the hook from the already-created store to ensure they share the same instance
        return partVersionStore.hook;
    },
    partVersionStore(_services) {
        // Create both the raw store and hook from the same instance
        const {store, hook} = createPartVersionStoreAndHook();
        // Return an object that has both the store API and the hook attached
        return Object.assign(store, {hook});
    },
    useAppStore({appStore}) {
        // Use the hook from the same store instance that appStore uses
        return appStore.hook;
    },
    appStore(_services) {
        // Create both the raw store and hook from the same instance
        // This ensures epics and React components share the same store
        const {store, hook} = createAppStoreAndHook();
        return Object.assign(store, {hook});
    },
    useDocumentMetaStore(_services) {
        return createDocumentMetaStore();
    },
    usePartStore({partStore}) {
        // Use the hook from the same store instance that partStore uses
        return partStore.hook;
    },
    partStore(_services) {
        // Create both the raw store and hook from the same instance
        // This ensures services and React components share the same store
        const {store, hook} = createPartStoreAndHook();
        return Object.assign(store, {hook});
    },
    useChangeHistoryStore({changeHistoryStore}) {
        // Use the hook from the same store instance that changeHistoryStore uses
        return changeHistoryStore.hook;
    },
    changeHistoryStore(_services) {
        // Create both the raw store and hook from the same instance
        // This ensures epics and React components share the same store
        const {store, hook} = createChangeHistoryStoreAndHook();
        return Object.assign(store, {hook});
    },
    useDocumentStore({documentStore}) {
        // Use the hook from the same store instance that documentStore uses
        return documentStore.hook;
    },
    documentStore({currentUserService}) {
        // Reset shared state for this container instance to prevent cross-contamination
        // when containers are recreated (e.g., in tests).
        sharedDocumentServices = {};
        sharedActionEventBus = new ActionEventBus();
        const {store, hook, undoRedoStore} = createDocumentStoreAndHook(
            currentUserService,
            sharedDocumentServices,
            sharedActionEventBus,
        );
        sharedUndoRedoStore = undoRedoStore;
        return Object.assign(store, {hook});
    },
    undoRedoStore({documentStore: _documentStore}) {
        // Accessing documentStore triggers its creation, which populates sharedUndoRedoStore.
        return sharedUndoRedoStore;
    },
    actionEventBus({documentStore}) {
        return sharedActionEventBus;
    },

    // Other
    logger() {
        const logger = createPinoLoggerForEnvironment();
        setGlobalFallbackLogger(logger);
        return logger;
    },
    clipboardService({logger}) {
        return new ClipboardService(logger);
    },
    launchDarklyService(services) {
        return new LaunchDarklyService(
            () => services.currentUserService,
            () => services.useOrganizationStore,
            services.logger,
        );
    },

    // Misc
    clientIdProvider() {
        return new ClientIdProvider();
    },

    pricingDataCache({firestoreAdapter}) {
        return new FirestoreCache(firestoreAdapter.pricingDataCacheCollection());
    },

    pricingService({pricingDataCache, functionsAdapter, analyticsStorage, logger}) {
        return new ClientPricingService(pricingDataCache, functionsAdapter, analyticsStorage, logger);
    },

    copilotFunctions({functionsAdapter}) {
        return {
            async evaluateChatThreadSatisfaction(payload: {
                threadUid: string;
                documentUid: string;
                rating: number;
                feedback?: string;
            }): Promise<void> {
                await functionsAdapter.evaluateChatThreadSatisfaction(payload);
            },

            async searchSystemKnowledgeModerationQueue(payload: {
                query: string;
                similarityThreshold?: number;
                limit?: number;
                statusFilter?: "all" | "pending" | "approved" | "declined";
                dateFilter?: "all" | "today" | "last3days" | "last7days" | "last30days";
                sortBy?: "created_at" | "updated_at" | "moderation_status";
                sortOrder?: "asc" | "desc";
            }): Promise<{
                results: {
                    uid: string;
                    score: number;
                    use_when: string;
                    knowledge: string;
                    metadata: {
                        uid?: string;
                        knowledge?: string;
                        user_uid?: string;
                        project_uid?: string;
                        context?: string;
                    };
                }[];
            }> {
                const result = await functionsAdapter.searchSystemKnowledgeModerationQueue(payload);
                return result.data;
            },

            async searchSystemKnowledgeBase(payload: {
                query: string;
                similarityThreshold?: number;
                limit?: number;
            }): Promise<{
                results: {
                    uid: string;
                    score: number;
                    use_when: string;
                    knowledge: string;
                    metadata: {
                        uid?: string;
                        knowledge?: string;
                        user_uid?: string;
                        project_uid?: string;
                        context?: string;
                    };
                }[];
            }> {
                const result = await functionsAdapter.searchSystemKnowledgeBase(payload);
                return result.data;
            },

            async getAdminCreditReport(payload: {
                accountUid: string;
            }): Promise<import("@buildwithflux/models").CreditReport> {
                return await functionsAdapter.getAdminCreditReport(payload);
            },

            async getAdminCreditTransactions(payload: {
                accountUid: string;
                periodId: string;
                pageSize?: number;
                startAfter?: string;
            }) {
                return await functionsAdapter.getAdminCreditTransactions(payload);
            },

            async getAdminStripeCustomerId(payload: {
                accountUid: string;
            }): Promise<import("@buildwithflux/firebase-functions-adapter").GetAdminStripeCustomerIdResponseData> {
                return await functionsAdapter.getAdminStripeCustomerId(payload);
            },

            async setAdminFreeCredits(payload: {
                accountUid: string;
                freeCredits: import("@buildwithflux/models").CreditAmount;
            }): Promise<void> {
                await functionsAdapter.setAdminFreeCredits(payload);
            },

            async sandbox(
                payload: import("@buildwithflux/firebase-functions-adapter").SandboxRequestData,
            ): Promise<import("@buildwithflux/firebase-functions-adapter").SandboxResponseData> {
                return await functionsAdapter.sandbox(payload);
            },

            async generateCss(
                payload: import("@buildwithflux/firebase-functions-adapter").GenerateCssRequestData,
            ): Promise<import("@buildwithflux/firebase-functions-adapter").GenerateCssResponseData> {
                return await functionsAdapter.generateCss(payload);
            },
        };
    },
};
